import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { RedImmobile } from '../../types/red'
import { FASE_CONFIG, EUR } from '../../constants/red'
import { redApi } from '../../services/api'

import TabAnagrafica from './tabs/TabAnagrafica'
import TabDocumenti from './tabs/TabDocumenti'
import TabPagamenti from './tabs/TabPagamenti'
import TabAppalto from './tabs/TabAppalto'
import TabSAL from './tabs/TabSAL'
import TabScadenzario from './tabs/TabScadenzario'
import TabFinanziamenti from './tabs/TabFinanziamenti'
import TabPraticheEdilizie from './tabs/TabPraticheEdilizie'
import TabRendicontazioni from './tabs/TabRendicontazioni'
import TabFotoCantiere from './tabs/TabFotoCantiere'
import TabValutazioni from './tabs/TabValutazioni'
import TabBadgeCantiere from './tabs/TabBadgeCantiere'
import TabSimulazione from './tabs/TabSimulazione'
import TabReportAnac from './tabs/TabReportAnac'
import TabMappa from './tabs/TabMappa'
import TabFirmaDigitale from './tabs/TabFirmaDigitale'
import TabAvanzamento from './tabs/TabAvanzamento'

const TABS = [
  { id: 'anagrafica',       label: 'Anagrafica' },
  { id: 'avanzamento',      label: 'Avanzamento' },
  { id: 'documenti',        label: 'Documenti' },
  { id: 'pagamenti',        label: 'Pagamenti' },
  { id: 'finanziamenti',    label: 'Finanziamenti' },
  { id: 'appalto',          label: 'Appalto IMECO' },
  { id: 'sal',              label: 'SAL' },
  { id: 'pratiche',         label: 'Pratiche Edilizie' },
  { id: 'rendicontazioni',  label: 'Rendicontazioni' },
  { id: 'foto',             label: 'Foto Cantiere' },
  { id: 'mappa',            label: 'Mappa' },
  { id: 'badge',            label: 'Badge Cantiere' },
  { id: 'simulazione',      label: 'Simulazione' },
  { id: 'valutazioni',      label: 'Valutazioni' },
  { id: 'report_anac',      label: 'Report ANAC' },
  { id: 'firma_digitale',   label: 'Firma Digitale' },
  { id: 'scadenzario',      label: 'Scadenzario' },
]

const RedImmobileDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('anagrafica')
  const [immobile, setImmobile] = useState<RedImmobile | null>(null)
  const [loading, setLoading] = useState(true)
  const [avanzandoFase, setAvanzandoFase] = useState(false)

  const reload = () => {
    if (!id) return
    redApi.immobili.get(id).then(res => {
      setImmobile(res.data)
      setLoading(false)
    })
  }

  useEffect(reload, [id])

  const handleAvanzaFase = async () => {
    if (!id) return
    setAvanzandoFase(true)
    try {
      const res = await redApi.immobili.avanzaFase(id)
      reload()
      const mancanti = res.data?.documenti_mancanti_alert as string[] | undefined
      if (mancanti && mancanti.length > 0) {
        toast.error(`Fase avanzata, ma mancano documenti vincolanti: ${mancanti.join(', ')}. Alert registrato in Agenda RED.`, { duration: 8000 })
      } else {
        toast.success('Fase avanzata')
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail
      if (detail?.missing) {
        toast.error(`Documenti mancanti: ${detail.missing.join(', ')}`)
      } else {
        toast.error('Errore avanzamento fase')
      }
    } finally {
      setAvanzandoFase(false)
    }
  }

  if (loading || !immobile) return <div className="p-8 text-gray-500">Caricamento...</div>

  const fase = FASE_CONFIG[immobile.fase_corrente]
  const completamento = immobile.calcoli?.completamento_documentale ?? 0

  return (
    <div className="flex flex-col h-full">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-start justify-between">
          <div>
            <button onClick={() => navigate('/red')}
              className="text-xs text-gray-400 hover:text-gray-600 mb-1">
              ← Portfolio
            </button>
            <h1 className="text-xl font-bold text-gray-900">{immobile.nome_riferimento}</h1>
            <p className="text-sm text-gray-500">
              {immobile.comune} ({immobile.provincia})
              {immobile.pratica_usr?.numero_pratica && ` · Pratica USR: ${immobile.pratica_usr.numero_pratica}`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium px-3 py-1 rounded-full"
              style={{ color: fase.color, backgroundColor: fase.bg }}>
              {fase.label}
            </span>
            {immobile.fase_corrente !== 'completato' && (
              <button
                onClick={handleAvanzaFase}
                disabled={avanzandoFase || completamento < 100}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40"
              >
                {avanzandoFase ? '...' : 'Avanza Fase →'}
              </button>
            )}
            <button
              onClick={async () => {
                if (!confirm('Sei sicuro di voler eliminare questo immobile? Verranno cancellati anche tutti i documenti, pagamenti, appalto, SAL e scadenze collegati.')) return
                try {
                  await redApi.immobili.delete(immobile.id)
                  toast.success('Immobile eliminato')
                  navigate('/red')
                } catch {
                  toast.error('Errore eliminazione immobile')
                }
              }}
              className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              🗑 Elimina
            </button>
          </div>
        </div>

        <div className="flex gap-4 mt-4 text-sm flex-wrap">
          <MetricBox label="Acquisizione" value={EUR.format(immobile.calcoli?.costo_totale_acquisizione ?? 0)} />
          <MetricBox label="Contributo USR" value={EUR.format(immobile.calcoli?.finanziamento_usr_totale ?? 0)} color="text-blue-600" />
          <MetricBox label="Erogato USR" value={EUR.format(immobile.calcoli?.erogato_usr_a_oggi ?? 0)} color="text-green-600" />
          <MetricBox label="Residuo USR" value={EUR.format(immobile.calcoli?.residuo_usr_da_erogare ?? 0)} color="text-orange-600" />
          {(immobile.calcoli?.penale_imeco_maturata ?? 0) > 0 && (
            <MetricBox label="Penale IMECO" value={EUR.format(immobile.calcoli!.penale_imeco_maturata!)} color="text-red-600" />
          )}
        </div>
      </div>

      <div className="bg-white border-b border-gray-200 px-6">
        <div className="flex gap-0 overflow-x-auto">
          {TABS.map(tab => (
            <button key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {activeTab === 'anagrafica'       && <TabAnagrafica key={immobile.id} immobile={immobile} onUpdate={reload} />}
        {activeTab === 'avanzamento'      && <TabAvanzamento immobile={immobile} onUpdate={reload} />}
        {activeTab === 'documenti'        && <TabDocumenti immobile={immobile} onUpdate={reload} />}
        {activeTab === 'pagamenti'        && <TabPagamenti immobile={immobile} onUpdate={reload} />}
        {activeTab === 'finanziamenti'    && <TabFinanziamenti immobile={immobile} onUpdate={reload} />}
        {activeTab === 'appalto'          && <TabAppalto immobile={immobile} onUpdate={reload} />}
        {activeTab === 'sal'              && <TabSAL immobile={immobile} onUpdate={reload} />}
        {activeTab === 'pratiche'         && <TabPraticheEdilizie immobile={immobile} onUpdate={reload} />}
        {activeTab === 'rendicontazioni'  && <TabRendicontazioni immobile={immobile} onUpdate={reload} />}
        {activeTab === 'foto'             && <TabFotoCantiere immobile={immobile} onUpdate={reload} />}
        {activeTab === 'mappa'            && <TabMappa immobile={immobile} onUpdate={reload} />}
        {activeTab === 'badge'            && <TabBadgeCantiere immobile={immobile} onUpdate={reload} />}
        {activeTab === 'simulazione'      && <TabSimulazione immobile={immobile} onUpdate={reload} />}
        {activeTab === 'valutazioni'      && <TabValutazioni immobile={immobile} onUpdate={reload} />}
        {activeTab === 'report_anac'      && <TabReportAnac immobile={immobile} onUpdate={reload} />}
        {activeTab === 'firma_digitale'   && <TabFirmaDigitale immobile={immobile} onUpdate={reload} />}
        {activeTab === 'scadenzario'      && <TabScadenzario immobile={immobile} onUpdate={reload} />}
      </div>
    </div>
  )
}

const MetricBox: React.FC<{ label: string; value: string; color?: string }> = ({ label, value, color = 'text-gray-900' }) => (
  <div className="bg-gray-50 rounded-lg px-3 py-2">
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`font-semibold ${color}`}>{value}</div>
  </div>
)

export default RedImmobileDetail
